import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useEffectiveRole } from '@/stores/authStore';
import { useAuth } from '@clerk/clerk-react';
import {
  AlertCircle,
  Download,
  FileImage,
  FileText,
  FileVideo,
  Loader2,
  Package,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://erg-shoot.des-466.workers.dev/api';

interface Asset {
  id: string;
  name: string;
  category: string;
  file_type: string | null;
  download_count: number;
  created_at: string;
}

interface AssetsResponse {
  success: boolean;
  data: {
    assets: Asset[];
    categories: Record<string, number>;
    pagination: {
      has_more: boolean;
      next_cursor: string | null;
    };
    total_count: number;
    is_empty?: boolean;
    message?: string;
  };
}

// File type options - supported formats
const typeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'image', label: 'Images' },
  { value: 'pdf', label: 'PDF Documents' },
  { value: 'video', label: 'Videos' },
  { value: 'zip', label: 'ZIP Archives' },
];

// Role-based folder structure
// Maps to R2 prefixes and permission matrix
const allFolderOptions = [
  { value: 'all', label: 'All Folders', roles: ['hq_admin'] },
  { value: 'hq-admin', label: 'HQ Admin', roles: ['hq_admin'] },
  { value: 'agents', label: 'Agents', roles: ['hq_admin', 'agent'] },
  {
    value: 'affiliates',
    label: 'Affiliates',
    roles: ['hq_admin', 'agent', 'affiliate_owner', 'affiliate_coach'],
  },
  {
    value: 'general',
    label: 'General',
    roles: ['hq_admin', 'agent', 'affiliate_owner', 'affiliate_coach'],
  },
];

// Get folder options filtered by user role
function getFolderOptionsForRole(role: string | undefined): { value: string; label: string }[] {
  if (!role) return [];
  return allFolderOptions.filter((folder) => folder.roles.includes(role));
}

// HQ Admin upload folder options (can upload to any folder)
const uploadFolderOptions = [
  { value: 'hq-admin', label: 'HQ Admin Only' },
  { value: 'agents', label: 'Agents' },
  { value: 'affiliates', label: 'Affiliates (Owners & Coaches)' },
  { value: 'general', label: 'General (All Roles)' },
];

// Accepted file types for upload
const ACCEPTED_TYPES = {
  image: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'],
  pdf: ['application/pdf'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
  zip: ['application/zip', 'application/x-zip-compressed'],
};

const ALL_ACCEPTED = [
  ...ACCEPTED_TYPES.image,
  ...ACCEPTED_TYPES.pdf,
  ...ACCEPTED_TYPES.video,
  ...ACCEPTED_TYPES.zip,
].join(',');

export default function Assets() {
  const { getToken } = useAuth();
  const effectiveRole = useEffectiveRole();
  const isAdmin = effectiveRole === 'hq_admin';
  const isAgent = effectiveRole === 'agent';
  const isOwner = effectiveRole === 'affiliate_owner';
  // Admins, Agents, and Affiliate Owners can upload assets
  const canUpload = isAdmin || isAgent || isOwner;

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadFolder, setUploadFolder] = useState('general');
  const [assetTitle, setAssetTitle] = useState('');
  const [assetDescription, setAssetDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get folder options filtered by effective role (considers View-as mode)
  const folderOptions = getFolderOptionsForRole(effectiveRole);
  const defaultFolder = isAdmin ? 'all' : 'general';

  // Assets list state
  const [assets, setAssets] = useState<Asset[]>([]);
  const [_categories, setCategories] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(defaultFolder);
  const [selectedType, setSelectedType] = useState('all');

  // Fetch assets from API
  const fetchAssets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (selectedFolder !== 'all') {
        params.append('category', selectedFolder);
      }
      const url = `${API_URL}/assets${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assets');
      }

      const data: AssetsResponse = await response.json();
      if (data.success) {
        // Exclude 'manuals' — those belong to the Manuals & Guides page only
        setAssets(data.data.assets.filter((a) => a.category !== 'manuals'));
        setCategories(data.data.categories);
      }
    } catch (err) {
      console.error('Failed to fetch assets:', err);
      setError('Failed to load assets. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [getToken, selectedFolder]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!assetTitle) {
        setAssetTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!assetTitle) {
        setAssetTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !assetTitle) return;

    setIsUploading(true);
    setUploadError(null);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', assetTitle);
      formData.append('folder', uploadFolder);
      formData.append('category', uploadFolder.split('/')[0] || 'general'); // Use parent folder as category
      if (assetDescription) {
        formData.append('description', assetDescription);
      }

      const response = await fetch(`${API_URL}/assets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      // Reset form and close modal
      setSelectedFile(null);
      setAssetTitle('');
      setAssetDescription('');
      setUploadFolder('general');
      setShowUploadModal(false);

      // Refresh assets list
      fetchAssets();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(errorMessage);
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (asset: Asset) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/assets/${asset.id}/stream`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = asset.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Refresh to update download count
      fetchAssets();
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (asset: Asset) => {
    if (!confirm(`Are you sure you want to delete "${asset.name}"?`)) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/assets/${asset.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      // Refresh assets list
      fetchAssets();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <FileImage className="h-8 w-8 text-blue-500" />;
    if (file.type === 'application/pdf') return <FileText className="h-8 w-8 text-red-500" />;
    if (file.type.startsWith('video/')) return <FileVideo className="h-8 w-8 text-purple-500" />;
    if (file.type.includes('zip')) return <Package className="h-8 w-8 text-yellow-500" />;
    return <Download className="h-8 w-8 text-muted-foreground" />;
  };

  const getAssetIcon = (fileType: string | null) => {
    if (fileType === 'image') return <FileImage className="h-8 w-8 text-blue-500" />;
    if (fileType === 'pdf') return <FileText className="h-8 w-8 text-red-500" />;
    if (fileType === 'video') return <FileVideo className="h-8 w-8 text-purple-500" />;
    if (fileType === 'zip') return <Package className="h-8 w-8 text-yellow-500" />;
    return <Download className="h-8 w-8 text-muted-foreground" />;
  };

  // Filter assets by search query and type
  const filteredAssets = assets.filter((asset) => {
    const matchesSearch =
      !searchQuery || asset.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || asset.file_type === selectedType;
    return matchesSearch && matchesType;
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Assets</h1>
          <p className="text-muted-foreground">
            Download professional marketing materials, templates, and brand resources.
          </p>
        </div>
        {canUpload && (
          <Button
            onClick={() => {
              setUploadError(null);
              setShowUploadModal(true);
            }}
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Asset
          </Button>
        )}
      </div>

      {/* Filter Bar - Simplified with merged folder/category */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search assets by title..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          options={folderOptions}
          value={selectedFolder}
          onChange={(e) => setSelectedFolder(e.target.value)}
          className="w-full sm:w-[200px]"
        />
        <Select
          options={typeOptions}
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="w-full sm:w-[160px]"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading assets...</span>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <p className="text-destructive">{error}</p>
            <Button variant="outline" className="mt-4" onClick={fetchAssets}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && filteredAssets.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Assets</CardTitle>
            <CardDescription>Marketing materials and resources</CardDescription>
          </CardHeader>
          <CardContent className="text-center py-12">
            <Download className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No Assets Found</p>
            <p className="text-sm text-muted-foreground mt-2">
              {searchQuery || selectedFolder !== 'all' || selectedType !== 'all'
                ? 'Try adjusting your filters.'
                : isAdmin
                  ? 'Click "Upload Asset" to add marketing materials.'
                  : 'No marketing assets have been uploaded yet.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Assets Grid */}
      {!isLoading && !error && filteredAssets.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAssets.map((asset) => (
            <Card key={asset.id} className="group hover:border-primary/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 bg-muted rounded-lg">
                    {getAssetIcon(asset.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate" title={asset.name}>
                      {asset.name}
                    </h3>
                    <p className="text-sm text-muted-foreground capitalize">{asset.category}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {asset.download_count} downloads
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleDownload(asset)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDelete(asset)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Asset</DialogTitle>
            <DialogDescription>
              Upload marketing materials. Supported formats: Images, PDF, Video, ZIP.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upload Error */}
            {uploadError && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {uploadError}
              </div>
            )}

            {/* Drop Zone - uses div for drag-and-drop support */}
            <div
              className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              // biome-ignore lint/a11y/useSemanticElements: Drop zone div needs role for accessibility
              role="button"
              tabIndex={0}
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-3">
                    {getFileIcon(selectedFile)}
                    <div className="text-left">
                      <p className="font-medium truncate max-w-[200px]">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    Drag & drop a file here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Images, PDF, Video, ZIP (max 50MB)
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ALL_ACCEPTED}
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Asset Details */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={assetTitle}
                  onChange={(e) => setAssetTitle(e.target.value)}
                  placeholder="Asset title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Input
                  id="description"
                  value={assetDescription}
                  onChange={(e) => setAssetDescription(e.target.value)}
                  placeholder="Brief description of the asset"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="folder">Folder</Label>
                <Select
                  options={uploadFolderOptions}
                  value={uploadFolder}
                  onChange={(e) => setUploadFolder(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={!selectedFile || !assetTitle || isUploading}>
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
